import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, ExternalLink, Plus, Loader2, Trash2, TrendingUp, PoundSterling, Link2, Sparkles, RotateCw, ImagePlus } from "lucide-react";

// Curated dropshipping admin panel — lets the AfriMarket team add hand-picked
// African-themed products (sourced from Alibaba/AliExpress) under the
// "AfriMarket Official" seller account. Private supplier info (URL, cost,
// notes) stays admin-only thanks to the backend's separate collection.

const empty = {
  title: "", description: "", category: "Fashion", price: "", currency: "GBP",
  country: "United Kingdom", images: "",
  supplier_url: "", supplier_cost: "", supplier_notes: "",
  estimated_shipping_min_days: 14, estimated_shipping_max_days: 28,
};

const CuratedListings = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/curated-listings");
      setList(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load curated listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        supplier_cost: form.supplier_cost ? parseFloat(form.supplier_cost) : null,
        images: form.images.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/admin/curated-listings", payload);
      toast.success("Curated listing created 🎉");
      setForm(empty);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create listing");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this curated listing? Buyers won't see it anymore.")) return;
    try {
      await api.delete(`/admin/curated-listings/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const [seeding, setSeeding] = useState(false);
  const seedStarterPack = async () => {
    if (!window.confirm("Seed the 13-product starter catalogue? Existing listings with the same titles will be skipped.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/curated-listings/seed-starter-pack");
      toast.success(`Starter pack seeded: ${data.created} new, ${data.skipped} skipped (already present)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed starter pack");
    } finally {
      setSeeding(false);
    }
  };

  const [seedingTrending, setSeedingTrending] = useState(false);
  const seedTrending2026 = async () => {
    if (!window.confirm("Seed the 12-product Trending 2026 catalogue (electronics + African fashion + beauty)? Existing listings with the same titles will be skipped.")) return;
    setSeedingTrending(true);
    try {
      const { data } = await api.post("/admin/curated-listings/seed-trending-2026");
      toast.success(`Trending 2026 seeded: ${data.created} new, ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed trending pack");
    } finally {
      setSeedingTrending(false);
    }
  };

  // ── Destructive: wipe + re-seed everything ────────────────────────────────
  const [forceReseeding, setForceReseeding] = useState(false);
  const forceReseed = async () => {
    const typed = window.prompt(
      "⚠️ This DELETES every curated listing without paid orders, then re-seeds both packs from scratch.\n\nType REPLACE (in CAPS) to continue.",
      "",
    );
    if (typed !== "REPLACE") {
      if (typed !== null) toast.error("Cancelled — you didn't type REPLACE.");
      return;
    }
    setForceReseeding(true);
    try {
      const { data } = await api.post("/admin/curated-listings/force-reseed", { confirm: "REPLACE" });
      const starter = data.starter_pack || {};
      const trending = data.trending_2026 || {};
      toast.success(
        `Wiped ${data.wiped} · Starter ${starter.created || 0} new · Trending ${trending.created || 0} new` +
        (data.preserved_with_orders ? ` · ${data.preserved_with_orders} preserved (have orders)` : ""),
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to force re-seed");
    } finally {
      setForceReseeding(false);
    }
  };

  // ── Top-up images so every curated listing has 4+ photos ─────────────────
  const [toppingUp, setToppingUp] = useState(false);
  const topupImages = async () => {
    setToppingUp(true);
    toast.message("Generating photos — this can take 1-2 minutes…");
    try {
      const { data } = await api.post(
        "/admin/curated-listings/topup-images",
        { target: 4 },
        { timeout: 240000 },  // 4-min client timeout — image gen is slow
      );
      toast.success(
        `Topped up ${data.topped_up} listings · generated ${data.generated} new images · ${data.already_ok} already had 4+`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to top-up images");
    } finally {
      setToppingUp(false);
    }
  };


  // ── URL auto-import — paste any AliExpress/CJ/Amazon link → fill the form ──
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const runImport = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/curated-listings/import-url", { url });
      const imgs = (data.images || []).join("\n");
      // Map common currencies to symbols-aware "GBP" so the form's currency stays GBP by default.
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        images: imgs || f.images,
        price: data.price ? String(data.price) : f.price,
        supplier_url: url,
      }));
      setShowForm(true);
      toast.success(`Imported "${(data.title || "product").slice(0, 50)}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not import from that URL");
    } finally {
      setImporting(false);
    }
  };

  const totalRevenue = list.reduce((sum, x) => sum + (x.revenue || 0), 0);
  const totalSales = list.reduce((sum, x) => sum + (x.sales_count || 0), 0);
  const totalEstMargin = list.reduce((sum, x) => {
    const m = x.estimated_margin;
    return sum + (m ? m * (x.sales_count || 0) : 0);
  }, 0);

  return (
    <Card className="p-6 bg-white border-black/10" data-testid="curated-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#C05A3C] text-white flex items-center justify-center">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline text-[#C05A3C]">Dropshipping</div>
            <h3 className="font-display text-2xl leading-tight">Curated by AfriMarket</h3>
            <div className="text-xs text-muted-foreground">
              Hand-picked African-themed products under the AfriMarket Official account
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={seedStarterPack}
            disabled={seeding}
            data-testid="curated-seed-btn"
            className="border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10"
          >
            {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
            Seed starter pack
          </Button>
          <Button
            variant="outline"
            onClick={seedTrending2026}
            disabled={seedingTrending}
            data-testid="curated-seed-trending-btn"
            className="border-[#C05A3C] text-[#C05A3C] hover:bg-[#C05A3C]/10"
          >
            {seedingTrending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
            Seed Trending 2026
          </Button>
          <Button
            variant="outline"
            onClick={forceReseed}
            disabled={forceReseeding}
            data-testid="curated-force-reseed-btn"
            className="border-red-300 text-red-700 hover:bg-red-50"
            title="Wipe all unpurchased curated listings then re-seed both packs"
          >
            {forceReseeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
            Force re-seed
          </Button>
          <Button
            variant="outline"
            onClick={topupImages}
            disabled={toppingUp}
            data-testid="curated-topup-images-btn"
            className="border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10"
            title="Ensure every curated listing has at least 4 photos — generates extra AI angles for any item below the target"
          >
            {toppingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
            Top-up to 4 photos
          </Button>
          <Button
            onClick={() => setShowForm((v) => !v)}
            className="bg-[#1A2024] text-white hover:bg-[#1A2024]/90"
            data-testid="curated-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {showForm ? "Cancel" : "Add curated listing"}
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Listings" value={list.length} testid="curated-stat-listings" />
        <Stat label="Sales" value={totalSales} testid="curated-stat-sales" icon={<TrendingUp className="w-3 h-3" />} />
        <Stat label="Revenue" value={`£${totalRevenue.toFixed(2)}`} testid="curated-stat-revenue" icon={<PoundSterling className="w-3 h-3" />} />
        <Stat label="Est. profit" value={`£${totalEstMargin.toFixed(2)}`} testid="curated-stat-profit" accent="text-[#4A5D23]" />
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={submit} className="bg-[#F3EFE9] p-4 rounded-md mb-5 space-y-3" data-testid="curated-form">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Title (public)" required>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="curated-input-title" required />
            </Field>
            <Field label="Category" required>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Fashion / Beauty / Home decor / …" data-testid="curated-input-category" />
            </Field>
            <Field label="Selling price (£)" required>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="curated-input-price" required />
            </Field>
            <Field label="Country (display only)">
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="curated-input-country" />
            </Field>
          </div>
          <Field label="Description (public)" required>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="curated-input-desc" required />
          </Field>
          <Field label="Image URLs (one per line or comma-separated)">
            <Textarea value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={2} data-testid="curated-input-images" placeholder="https://… (paste Alibaba/AliExpress image URLs here)" />
          </Field>

          <div className="border-t border-black/10 pt-3">
            <div className="label-overline text-[#C05A3C] mb-2">Private supplier info (admin-only)</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Supplier URL (Alibaba/AliExpress)">
                <Input value={form.supplier_url} onChange={(e) => setForm({ ...form, supplier_url: e.target.value })} placeholder="https://www.aliexpress.com/item/…" data-testid="curated-input-supplier-url" />
              </Field>
              <Field label="Your cost (£)">
                <Input type="number" step="0.01" value={form.supplier_cost} onChange={(e) => setForm({ ...form, supplier_cost: e.target.value })} placeholder="e.g. 12.50" data-testid="curated-input-supplier-cost" />
              </Field>
            </div>
            <Field label="Supplier notes (admin-only)">
              <Textarea value={form.supplier_notes} onChange={(e) => setForm({ ...form, supplier_notes: e.target.value })} rows={2} placeholder="e.g. 'Best supplier: Yiwu Textile Co., 18-day shipping, requires bulk pack of 5'" data-testid="curated-input-supplier-notes" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min shipping days">
                <Input type="number" value={form.estimated_shipping_min_days} onChange={(e) => setForm({ ...form, estimated_shipping_min_days: parseInt(e.target.value || "14", 10) })} data-testid="curated-input-ship-min" />
              </Field>
              <Field label="Max shipping days">
                <Input type="number" value={form.estimated_shipping_max_days} onChange={(e) => setForm({ ...form, estimated_shipping_max_days: parseInt(e.target.value || "28", 10) })} data-testid="curated-input-ship-max" />
              </Field>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="bg-[#C05A3C] text-white hover:bg-[#C05A3C]/90" data-testid="curated-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Publish curated listing
          </Button>
        </form>
      )}

      {/* List of existing curated */}
      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-muted-foreground italic" data-testid="curated-empty">
          No curated listings yet. Click &ldquo;Add curated listing&rdquo; above to seed the marketplace with Alibaba-sourced African-themed products.
        </div>
      ) : (
        <div className="space-y-3" data-testid="curated-list">
          {list.map((l) => (
            <div key={l.listing_id} className="flex items-start gap-3 p-3 border border-black/10 rounded-md bg-white" data-testid={`curated-row-${l.listing_id}`}>
              <img src={(l.images || [])[0] || "/img/placeholder.png"} alt={l.title} className="w-16 h-16 object-cover rounded" onError={(e) => { e.target.src = "/img/placeholder.png"; }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <a href={`/listing/${l.listing_id}`} target="_blank" rel="noreferrer" className="font-semibold text-[#1A2024] hover:underline">{l.title}</a>
                  <Badge className="bg-[#C05A3C] text-white text-[10px]">£{(l.price || 0).toFixed(2)}</Badge>
                  {l.sales_count > 0 && <Badge className="bg-[#4A5D23] text-white text-[10px]">{l.sales_count} sold</Badge>}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                  <span>Category: <strong>{l.category}</strong></span>
                  {l.supplier_cost && <span>Cost: <strong>£{l.supplier_cost.toFixed(2)}</strong></span>}
                  {l.estimated_margin && <span className="text-[#4A5D23]">Margin: <strong>£{l.estimated_margin.toFixed(2)}</strong></span>}
                  <span>Ships: <strong>{l.estimated_shipping_min_days}-{l.estimated_shipping_max_days}d</strong></span>
                </div>
                {l.supplier_url && (
                  <a href={l.supplier_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#C05A3C] hover:underline mt-1">
                    <ExternalLink className="w-3 h-3" /> Open supplier
                  </a>
                )}
                {l.supplier_notes && <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">📝 {l.supplier_notes}</div>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(l.listing_id)} data-testid={`curated-delete-${l.listing_id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#1A2024] mb-1 block">{label}{required && <span className="text-[#C05A3C]"> *</span>}</span>
    {children}
  </label>
);

const Stat = ({ label, value, testid, icon, accent = "text-[#1A2024]" }) => (
  <div className="bg-[#F3EFE9] rounded-md p-3" data-testid={testid}>
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
    <div className={`font-display text-xl ${accent}`}>{value}</div>
  </div>
);

export default CuratedListings;
